import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface GearAuditLogEntry {
  id: string;
  gear_item_id: string;
  project_id: string;
  action: "insert" | "update" | "delete";
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  changed_by: string;
  changed_at: string;
  item_name: string | null;
  changed_by_email: string | null;
}

export function useGearAuditLog(projectId: string) {
  return useQuery({
    queryKey: ["gear-audit-log", projectId],
    queryFn: async () => {
      if (!projectId) throw new Error("Project ID is required");

      const { data, error } = await supabase
        .from("gear_item_audit_log")
        .select(`
          id,
          gear_item_id,
          project_id,
          action,
          field_name,
          old_value,
          new_value,
          changed_by,
          changed_at
        `)
        .eq("project_id", projectId)
        .order("changed_at", { ascending: false })
        .limit(100);

      if (error) throw error;

      // Get gear item names and user emails separately to avoid join issues
      const gearItemIds = [...new Set(data?.map(entry => entry.gear_item_id).filter(Boolean))];
      const userIds = [...new Set(data?.map(entry => entry.changed_by).filter(Boolean))];

      const [gearItemsData, usersData] = await Promise.all([
        gearItemIds.length > 0 
          ? supabase.from("gear_items").select("id, name").in("id", gearItemIds)
          : { data: [], error: null },
        userIds.length > 0 
          ? supabase.from("profiles").select("id, email").in("id", userIds)
          : { data: [], error: null }
      ]);

      const gearItemsMap = new Map<string, string>();
      const usersMap = new Map<string, string>();
      
      gearItemsData.data?.forEach(item => gearItemsMap.set(item.id, item.name));
      usersData.data?.forEach(user => usersMap.set(user.id, user.email));

      // Transform the data to match our interface
      return (data || []).map((entry: any) => ({
        id: entry.id,
        gear_item_id: entry.gear_item_id,
        project_id: entry.project_id,
        action: entry.action,
        field_name: entry.field_name,
        old_value: entry.old_value,
        new_value: entry.new_value,
        changed_by: entry.changed_by,
        changed_at: entry.changed_at,
        item_name: gearItemsMap.get(entry.gear_item_id) || "Unknown Item",
        changed_by_email: usersMap.get(entry.changed_by) || "Unknown User",
      })) as GearAuditLogEntry[];
    },
    enabled: !!projectId,
    refetchInterval: 10000, // Refetch every 10 seconds to show recent changes
  });
}

export function useRecentProjectChanges(projectId: string, timeframeMinutes: number = 5) {
  return useQuery({
    queryKey: ["recent-project-changes", projectId, timeframeMinutes],
    queryFn: async () => {
      if (!projectId) throw new Error("Project ID is required");

      const cutoffTime = new Date();
      cutoffTime.setMinutes(cutoffTime.getMinutes() - timeframeMinutes);

      const { data, error } = await supabase
        .from("gear_item_audit_log")
        .select("id, changed_at, action")
        .eq("project_id", projectId)
        .gte("changed_at", cutoffTime.toISOString())
        .order("changed_at", { ascending: false });

      if (error) throw error;

      return {
        hasRecentChanges: data && data.length > 0,
        changeCount: data?.length || 0,
        lastChangeAt: data && data.length > 0 ? data[0].changed_at : null,
      };
    },
    enabled: !!projectId,
    refetchInterval: 5000, // Check for changes every 5 seconds
  });
}